import React from 'react';
import {
  coverageDisclaimer,
  PULSE_HERO_TAGLINE,
  PULSE_HERO_TITLE,
} from '../mapResponses';
import { pulseHero } from '../../../theme/dashboardLayout';
import { ProjectedDirection } from '../types';
import { clampPercent } from '../utils/clampPercent';
import { formatShortDate } from '../utils/formatDates';

interface Props {
  activeMembers?: number;
  cohortMembers?: number;
  enrolledMembers?: number;
  anchorDate?: string;
  score?: number;
  direction?: ProjectedDirection;
  wearablesPercent?: number | null;
  facePercent?: number | null;
  voicePercent?: number | null;
  windowDays?: number;
}

function HeroStat({
  label,
  value,
  ariaLabel,
  valueColor,
  className,
  footnoteMark,
  subvalue,
}: {
  label: string;
  value?: string;
  ariaLabel: string;
  valueColor?: string;
  className?: string;
  footnoteMark?: boolean;
  subvalue?: string;
}): React.ReactElement {
  return (
    <div
      aria-label={ariaLabel}
      className={
        `rounded-2xl bg-pure-white/10 px-4 py-3.5 text-pure-white ring-1 ring-inset ring-pure-white/10 ` +
        (className ? ` ${className}` : '')
      }
    >
      <p className="text-[13px] font-semibold leading-snug text-pure-white/70">
        {label}
        {footnoteMark ? (
          <sup className="ml-0.5 font-semibold" aria-hidden="true">*</sup>
        ) : null}
      </p>
      <p
        className="mt-1.5 font-sans-co text-xl font-bold leading-tight"
        style={valueColor ? { color: valueColor } : undefined}
      >
        {value}
      </p>
      {subvalue ? (
        <p className="mt-1 text-[10px] font-medium tracking-wide text-pure-white/55">{subvalue}</p>
      ) : null}
    </div>
  );
}

function enrolledShare(count: number, enrolled: number): string {
  if (enrolled <= 0) {
    return '0%';
  }

  return `${Math.round((count / enrolled) * 100)}%`;
}

function peopleNoun(count: number): string {
  return count === 1 ? 'person' : 'people';
}

function haveVerb(count: number): string {
  return count === 1 ? 'has' : 'have';
}

function recentSignalsLine(active: number, enrolled: number, windowDays: number): string {
  return `In the last ${windowDays} days, ${active} ${peopleNoun(active)} ${haveVerb(active)} signals - ${enrolledShare(active, enrolled)} of enrolled members`;
}

function sinceBeginningLine(cohort: number, enrolled: number): string {
  return `Since the first day, ${cohort} ${peopleNoun(cohort)} ${haveVerb(cohort)} signals - ${enrolledShare(cohort, enrolled)} of enrolled members`;
}

function PulseHero(props: Props): React.ReactElement {
  const wearables = clampPercent(props.wearablesPercent);
  const face = clampPercent(props.facePercent);
  const voice = clampPercent(props.voicePercent);
  const windowDays = props.windowDays ?? 28;
  const activeMembers = props.activeMembers;
  const cohortMembers = props.cohortMembers;
  const enrolledMembers = props.enrolledMembers;
  const memberLines = activeMembers != null && cohortMembers != null && enrolledMembers != null
    ? {
      recent: recentSignalsLine(activeMembers, enrolledMembers, windowDays),
      since: sinceBeginningLine(cohortMembers, enrolledMembers),
    }
    : null;
  const showSignalMembershipLines = false;
  const showCoverage = props.wearablesPercent != null
    || props.facePercent != null
    || props.voicePercent != null;

  return (
    <header className={pulseHero}>
      <div className="lg:flex lg:items-center lg:justify-between lg:gap-10">
      <div className="min-w-0 max-w-2xl">
        <h1
          className="font-sans-co text-[2rem] font-bold leading-tight text-pure-white sm:text-[3rem]"
          aria-label={`${PULSE_HERO_TITLE}™`}
        >
          {PULSE_HERO_TITLE}
          <sup className="align-super text-[0.4em] font-semibold tracking-wide" aria-hidden="true">
            TM
          </sup>
        </h1>
        <p className="mt-3 max-w-2xl text-p1 font-normal text-pure-white/85">
          {PULSE_HERO_TAGLINE}
        </p>
      </div>
      {showCoverage || (showSignalMembershipLines && memberLines != null) ? (
        <div
          role="group"
          aria-label="Dashboard snapshot"
          className="mt-8 grid w-full max-w-md shrink-0 grid-cols-2 gap-2 sm:max-w-none lg:mt-0 lg:w-[28rem]"
        >
          {showCoverage ? (
            <HeroStat
              className="col-span-2"
              footnoteMark
              label={`Average daily share of people with each signal, last ${windowDays} days`}
              value={`Wearables ${wearables}% · Face ${face}% · Voice ${voice}%`}
              subvalue={props.anchorDate ? `as of ${formatShortDate(props.anchorDate)}` : undefined}
              ariaLabel={`Average daily share of people with each signal, last ${windowDays} days. Wearables ${wearables} Face ${face} Voice ${voice}.${props.anchorDate ? ` as of ${formatShortDate(props.anchorDate)}` : ''}`}
            />
          ) : null}
          {showSignalMembershipLines && memberLines != null ? (
            <div className="col-span-2 space-y-1 rounded-2xl bg-pure-white/10 px-4 py-3.5 text-p1 text-pure-white/80 ring-1 ring-inset ring-pure-white/10">
              <p className="font-semibold tabular-nums text-pure-white">
                {memberLines.recent}
              </p>
              <p className="font-semibold tabular-nums text-pure-white">
                {memberLines.since}
              </p>
              {props.anchorDate ? (
                <p className="text-[10px] font-medium tracking-wide text-pure-white/55">
                  as of {formatShortDate(props.anchorDate)}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      </div>
      {showCoverage ? (
        <p className="mt-6 text-[13px] font-normal leading-snug text-pure-white/70">
          <span aria-hidden="true">*</span>
          {' '}
          {coverageDisclaimer(windowDays)}
        </p>
      ) : null}
    </header>
  );
}

export default PulseHero;
